import { useServerFn } from "@tanstack/react-start";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import Papa from "papaparse";
import { bulkImportPosts } from "@/lib/blog.functions";

export const Route = createFileRoute("/_authenticated/admin/import")({
  component: ImportPage,
});

const TEMPLATE = `slug,title,excerpt,body_markdown,cover_image_url,author_name,tags,seo_title,seo_description,scheduled_for,status
example-post,Example Post Title,A short excerpt,"# Hello\\n\\nMarkdown body here.",https://example.com/cover.jpg,Kristan Cole Network,"recruiting|leadership",SEO Title,SEO description,2026-07-01T09:00:00Z,scheduled
`;

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/['"’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

type KCNRow = Record<string, string>;

// Detect a KCN quarterly content plan by the presence of "Week #" column.
function isKCNPlan(rows: any[]) {
  return rows.length > 0 && Object.keys(rows[0] ?? {}).some((k) => k.trim().toLowerCase().startsWith("week"));
}

function pick(row: KCNRow, ...names: string[]) {
  for (const n of names) {
    for (const key of Object.keys(row)) {
      if (key.trim().toLowerCase() === n.toLowerCase()) return (row[key] ?? "").trim();
    }
  }
  // fallback: substring match
  for (const n of names) {
    for (const key of Object.keys(row)) {
      if (key.trim().toLowerCase().includes(n.toLowerCase())) return (row[key] ?? "").trim();
    }
  }
  return "";
}

function mapKCN(raw: any[], startDate: string, daysOfWeek: number[], hour: number) {
  // Group rows by week number, preserving order within the week.
  const byWeek = new Map<number, KCNRow[]>();
  for (const r of raw as KCNRow[]) {
    const w = parseInt(pick(r, "Week #", "Week"), 10);
    if (!Number.isFinite(w)) continue;
    if (!pick(r, "Video Title", "SEO Title (<=65 chars)", "SEO Title")) continue;
    const list = byWeek.get(w) ?? [];
    list.push(r);
    byWeek.set(w, list);
  }

  if (!startDate || daysOfWeek.length === 0) return [];
  const start = new Date(startDate + "T00:00:00");

  const out: any[] = [];
  for (const [week, items] of [...byWeek.entries()].sort((a, b) => a[0] - b[0])) {
    items.forEach((r, idx) => {
      const dayOffset = daysOfWeek[idx % daysOfWeek.length] ?? daysOfWeek[0];
      const d = new Date(start);
      d.setDate(start.getDate() + (week - 1) * 7 + dayOffset);
      d.setHours(hour, 0, 0, 0);

      const title = pick(r, "SEO Title (<=65 chars)", "SEO Title", "Video Title");
      const slug = slugify(title);
      const tagsRaw = pick(r, "Tags (comma-separated)", "Tags");
      const blogBrief = pick(r, "Website Blog Post");
      const newsletter = pick(r, "Substack Newsletter Post");
      const question = pick(r, "Question Answered");
      const hook = pick(r, "Hook Concept (First 15 Seconds)", "Hook Concept");
      const cta = pick(r, "Call to Action (All Placements)", "Call to Action");

      const body = [
        question ? `> **${question}**\n` : "",
        blogBrief ? `## Outline\n\n${blogBrief}\n` : "",
        hook ? `## Hook\n\n${hook}\n` : "",
        newsletter ? `## Long-form draft (from newsletter)\n\n${newsletter}\n` : "",
        cta ? `## Call to action\n\n${cta}\n` : "",
        `\n<!-- Add the YouTube video embed here once published. -->\n`,
      ].filter(Boolean).join("\n");

      out.push({
        slug,
        title,
        excerpt: question || pick(r, "Thumbnail Text Overlay (3-6 words)") || null,
        body_markdown: body,
        cover_image_url: null,
        author_name: "Kristan Cole Network",
        tags: tagsRaw,
        seo_title: pick(r, "SEO Title (<=65 chars)", "SEO Title") || title,
        seo_description: pick(r, "SEO Description (150-300 words)", "SEO Description") || null,
        scheduled_for: d.toISOString(),
        status: "scheduled",
      });
    });
  }
  return out;
}

function ImportPage() {
  const importFn = useServerFn(bulkImportPosts);
  const [rawRows, setRawRows] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{ inserted: number } | null>(null);
  const [busy, setBusy] = useState(false);

  // KCN plan scheduling controls
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() + ((1 - d.getDay() + 7) % 7 || 7)); // next Monday
    return d.toISOString().slice(0, 10);
  });
  const [cadence, setCadence] = useState<"tue-thu" | "mon-wed" | "mon-thu" | "mon">("tue-thu");
  const [hour, setHour] = useState(9);

  const isKCN = useMemo(() => isKCNPlan(rawRows), [rawRows]);
  const daysOfWeek = useMemo(() => {
    // 0 = startDate day-of-week offset from Monday. We treat startDate as week-start.
    // Offsets are days after startDate.
    switch (cadence) {
      case "tue-thu": return [1, 3];
      case "mon-wed": return [0, 2];
      case "mon-thu": return [0, 3];
      case "mon": return [0];
    }
  }, [cadence]);

  const rows = useMemo(() => {
    if (!isKCN) return rawRows;
    return mapKCN(rawRows, startDate, daysOfWeek, hour);
  }, [rawRows, isKCN, startDate, daysOfWeek, hour]);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(null);
    setResult(null);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.replace(/^\uFEFF/, "").trim(),
      complete: (res) => {
        if (res.errors.length) setErr(res.errors[0].message);
        setRawRows(res.data as any[]);
      },
    });
  }

  async function submit() {
    if (rows.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await importFn({ data: { rows } });
      setResult({ inserted: r.inserted });
      setRawRows([]);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="headline-md mb-2">Import content plan</h1>
      <p className="text-foreground/75 mb-6 max-w-2xl">
        Upload a CSV of posts. Existing slugs are updated; new slugs are created.
        Each post can be drafted, scheduled, or published. Scheduled posts auto-publish via cron.
      </p>

      <details className="mb-6 border border-border bg-surface p-4">
        <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] text-foreground/75">Supported formats</summary>
        <div className="mt-3 text-sm text-foreground/75 space-y-3">
          <p>
            <strong className="text-foreground">KCN Quarterly Content Plan</strong> - auto-detected when a "Week #" column is present.
            The importer maps Video/SEO Title, SEO Description, Tags, Website Blog Post outline, Hook, Newsletter, and CTA into
            each post, generates slugs, and schedules from your start date + cadence below. Body is pre-filled as a draft outline
            so you can finalize copy and paste the YouTube embed before publish.
          </p>
          <p>
            <strong className="text-foreground">Standard format</strong> - columns: <code>slug, title, excerpt, body_markdown, cover_image_url, author_name, tags, seo_title, seo_description, scheduled_for, status</code>.
            Tags separated by <code>|</code> or <code>,</code>. Dates in ISO 8601.
          </p>
          <a
            className="inline-block text-xs uppercase tracking-[0.18em] text-primary hover:underline"
            download="kcn-content-plan-template.csv"
            href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEMPLATE)}`}
          >
            Download standard template
          </a>
        </div>
      </details>

      <input type="file" accept=".csv,text/csv" onChange={onFile} className="mb-4 text-sm" />

      {isKCN ? (
        <div className="border border-primary/40 bg-primary/5 p-4 mb-6">
          <p className="text-xs uppercase tracking-[0.18em] text-primary mb-3">KCN content plan detected</p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.14em] text-foreground/65 mb-1">Week 1 start date</span>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-background border border-border px-3 py-2" />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.14em] text-foreground/65 mb-1">Cadence (2 per week)</span>
              <select value={cadence} onChange={(e) => setCadence(e.target.value as any)}
                className="w-full bg-background border border-border px-3 py-2">
                <option value="tue-thu">Tue & Thu</option>
                <option value="mon-wed">Mon & Wed</option>
                <option value="mon-thu">Mon & Thu</option>
                <option value="mon">Monday only</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.14em] text-foreground/65 mb-1">Publish hour (local)</span>
              <input type="number" min={0} max={23} value={hour}
                onChange={(e) => setHour(parseInt(e.target.value || "9", 10))}
                className="w-full bg-background border border-border px-3 py-2" />
            </label>
          </div>
          <p className="text-xs text-foreground/65 mt-3">
            Days are offsets from the start date you pick - set start date to the Monday of week 1 to match the cadence labels.
          </p>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="border border-border mb-6 overflow-auto max-h-96">
          <table className="w-full text-xs">
            <thead className="bg-surface uppercase tracking-[0.14em] text-foreground/65">
              <tr>
                <th className="text-left p-2">Slug</th>
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Scheduled for</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, i: number) => (
                <tr key={i} className="border-t border-border">
                  <td className="p-2 font-mono">{r.slug}</td>
                  <td className="p-2">{r.title}</td>
                  <td className="p-2">{r.status || (r.scheduled_for ? "scheduled" : "draft")}</td>
                  <td className="p-2">{r.scheduled_for ? new Date(r.scheduled_for).toLocaleString() : " - "}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {err ? <p className="text-primary mb-4">{err}</p> : null}
      {result ? <p className="text-foreground mb-4">Imported {result.inserted} posts.</p> : null}

      <button
        onClick={submit}
        disabled={busy || rows.length === 0}
        className="bg-primary px-6 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
      >
        {busy ? "Importing…" : `Import ${rows.length} posts`}
      </button>
    </div>
  );
}
